import {
  executeMulticallViem,
  type EndpointFailure,
  MulticallConfig,
  multicallShardedAbiArray,
  multicallViemAbiArray,
  prepareMulticallInputs,
} from '../../../utils/multicall'
import { createMulticallRpcCall } from '../../../utils/rpcCall'
import {
  LenderUserQuery,
  organizeUserQueries,
} from '../../../types/lenderTypes'
import { GetEvmClientFunction, LenderData } from '../../../types'
import { getAbi } from '../abis'
import { PoolWithMeta, unflattenLenderData } from '../../../lending-pools'
import { getEvmChain, getEvmClientUniversal } from '@1delta/providers'
import { MULTICALL_DEFAULT_BATCH_SIZE } from '../../../utils/constants'
import { UserData } from '../utils/types'
import { buildUserCall } from './prepare'
import { convertLenderUserDataResult } from './parse'
import type {
  ChainQuery,
  ProviderOptions,
  PreparedUserDataRpcCalls,
} from './types'

/**
 * Builds the multicall calls for the given queries and returns the raw results
 * @param chainId - The chain ID
 * @param queriesRaw - The queries to fetch data for
 * @param getEvmClient - Injected function to get EVM client
 * @param allowFailure - multicall can fail in single call, default is true
 * @param batchSize - multicall batch size, default is 4096
 * @param logs - show multicall error logs, default is false
 * @param concurrency - number of distinct RPC endpoints to shard batches
 *   across in parallel; <= 1 keeps the legacy single-endpoint path
 * @param permanentFailures - optional collector filled with the indices of
 *   calls that failed DETERMINISTICALLY (revert / no code / unknown selector)
 *   rather than because of the RPC. Pass it to
 *   {@link convertLenderUserDataResult} so a caller can tell "this market
 *   always reverts" from "this read was lost" and only re-fetch the latter.
 * @param onEndpointFailure - optional hook invoked for every RPC endpoint that
 *   fails to serve a batch. Only the caller knows where to persist that (KV,
 *   metrics), and without it every request rediscovers the same bad endpoint.
 * @returns The raw results from the multicall, "0x" for failures
 */
export const getLenderUserDataResult = async (
  chainId: string,
  queriesRaw: LenderUserQuery[],
  getEvmClient: GetEvmClientFunction,
  allowFailure = true,
  batchSize = MULTICALL_DEFAULT_BATCH_SIZE,
  retries = 3,
  logs = false,
  concurrency = 1,
  permanentFailures?: Set<number>,
  onEndpointFailure?: (info: EndpointFailure) => void,
): Promise<any[]> => {
  const queries = organizeUserQueries(queriesRaw)

  const builtCalls = await Promise.all(
    queries.map(async (query) => {
      const abi = getAbi(query.lender)
      const callData = await buildUserCall(
        chainId,
        query.lender,
        query.account,
        query.params,
        getEvmClient,
      )
      return callData.map((call: any) => ({ call, abi: call.abi ?? abi }))
    }),
  )
  const calls = builtCalls.flat()

  return await multicallShardedAbiArray(
    chainId,
    calls.map((call) => call.abi),
    calls.map((call) => call.call),
    getEvmClient,
    concurrency,
    retries,
    allowFailure,
    batchSize,
    logs,
    // Retry individual failed slots: a dropped call here is indistinguishable
    // from "user has no position" downstream, so it is worth re-reading on
    // other endpoints before giving up.
    true,
    permanentFailures,
    onEndpointFailure ? { onEndpointFailure } : undefined,
  )
}

export const getLenderUserDataResultForwarded = async (
  chainId: string,
  queriesRaw: any[], // LenderUserQuery[]
  getEvmClient: GetEvmClientFunction,
  allowFailure = true,
  batchSize = MULTICALL_DEFAULT_BATCH_SIZE,
  retries = 3,
  logs = false,
): Promise<any[]> => {
  const queries = organizeUserQueries(queriesRaw)

  const builtCalls = await Promise.all(
    queries.map(async (query) => {
      const abi = getAbi(query.lender)
      const callData = await buildUserCall(
        chainId,
        query.lender,
        query.account,
        query.params,
        getEvmClient,
      )
      return callData.map((call: any) => ({ call, abi: call.abi ?? abi }))
    }),
  )
  const calls = builtCalls.flat()

  // 1. Prepare
  const preparedCalls = prepareMulticallInputs(
    calls.map((c) => c.abi),
    calls.map((c) => c.call),
  )

  // 2. Execute via viem
  const config: MulticallConfig = {
    chainId,
    allowFailure,
    batchSize,
    retries,
    providerId: 0,
    logs,
  }

  return executeMulticallViem(preparedCalls, config, getEvmClient)
}

/**
 * Prepares the RPC calls for fetching user data without executing them
 * Uses multicall3 aggregate3 to batch all calls into a single RPC call
 * This reduces multiple RPC calls to one, avoiding rate limiting
 * @param chainId - The chain ID
 * @param queriesRaw - The queries to fetch data for
 * @param batchSize - Multicall batch size, default is 4096
 * @param blockTag - Block tag for the RPC calls, default is 'latest'
 * @param allowFailure - Allow individual calls to fail within multicall, default is true
 * @returns The prepared RPC batches and metadata needed for parsing
 */
export const prepareLenderUserDataRpcCalls = async (
  chainId: string,
  queriesRaw: LenderUserQuery[],
  batchSize = MULTICALL_DEFAULT_BATCH_SIZE,
  blockTag = 'latest',
  allowFailure = true,
): Promise<PreparedUserDataRpcCalls> => {
  const multicallAddress = getEvmChain(chainId).contracts?.multicall3?.address!

  const queries = organizeUserQueries(queriesRaw)

  const builtCalls = await Promise.all(
    queries.map(async (query) => {
      const abi = getAbi(query.lender)
      const callData = await buildUserCall(
        chainId,
        query.lender,
        query.account,
        query.params,
      )
      return callData.map((call: any) => ({ call, abi: call.abi ?? abi }))
    }),
  )
  const calls = builtCalls.flat()

  // Prepare multicall inputs
  const preparedCalls = prepareMulticallInputs(
    calls.map((c) => c.abi),
    calls.map((c) => c.call),
  )

  // Create multicall RPC batches (single RPC call per batch)
  const batches = createMulticallRpcCall(
    preparedCalls,
    multicallAddress,
    batchSize,
    blockTag,
    allowFailure,
  )

  // Flatten all RPC calls from batches for convenience
  const rpcCalls = batches.map((batch) => batch.call)

  return {
    batches,
    preparedCalls,
    queries: queriesRaw,
    rpcCalls,
  }
}

const DEFAULT_OPTIONS: Partial<ProviderOptions> = {
  allowFailure: true,
  batchSize: MULTICALL_DEFAULT_BATCH_SIZE,
  retries: 3,
  logs: false,
}

/**
 * Fetch structured lender balance set for addresses
 * @param account general account as override in queries
 * @param chainQueries chain quieres -can only be {chainId} array, account required then
 * @param lenderState lender state data for rates
 * @returns lender user data map
 */
export async function getLenderUserDataMulti(
  account: string | undefined,
  chainQueries: ChainQuery[],
  lenderState: LenderData | PoolWithMeta[],
): Promise<{
  [chainId: string]: {
    [lender: string]: UserData
  }
}> {
  // unflatten the data if needed
  const parsedLenderState = Array.isArray(lenderState)
    ? unflattenLenderData(lenderState)
    : lenderState

  // clean inputs
  const cleanChainQueries = chainQueries.map(
    ({ chainId, queries, providerOptions }) => {
      // clean option
      const safeProviderOptions = {
        ...DEFAULT_OPTIONS,
        // override via the package
        getEvmClient: (chain: string, rpcId: number | undefined) =>
          getEvmClientUniversal({ chain, rpcId: rpcId! }),
        ...providerOptions,
      }
      // provide lender queries - if none provided, use all
      if (!queries || queries?.every((q) => !q.lender)) {
        const lenders = Object.keys(parsedLenderState[chainId].data)
        queries = lenders.map((lender) => ({ account: account!, lender }))
      }

      return {
        queries,
        providerOptions: safeProviderOptions,
        chainId,
      }
    },
  )

  // One collector per chain, so the converter can tell a market that always
  // reverts from a read the RPC lost. Without it a cross-margin lender would
  // void its whole set over a single permanently-reverting market.
  const permanentFailuresByChain = cleanChainQueries.map(
    () => new Set<number>(),
  )

  const promises = cleanChainQueries.map(
    async ({ chainId, queries, providerOptions }, i) => {
      return await getLenderUserDataResult(
        chainId,
        queries,
        providerOptions.getEvmClient,
        providerOptions.allowFailure,
        providerOptions.batchSize,
        providerOptions.retries,
        providerOptions.logs,
        1,
        permanentFailuresByChain[i],
      )
    },
  )

  const data = await Promise.all(promises)

  const returndata: {
    [chainId: string]: {
      [lender: string]: UserData
    }
  } = {}

  cleanChainQueries.forEach(({ chainId, queries, providerOptions }, i) => {
    try {
      const dataForChain = convertLenderUserDataResult(
        chainId,
        queries,
        data[i],
        parsedLenderState as any,
        { permanentFailures: permanentFailuresByChain[i] },
      )
      returndata[chainId] = dataForChain
    } catch (e) {
      if (providerOptions.logs) {
        console.warn('error parsing for ' + chainId, e)
      }
    }
  })

  return returndata
}
